import * as React from "react";
import { useQuery } from "@tanstack/react-query";
import { fetchOrderById, updateOrderStatus } from "@/lib/api";
import { format } from "date-fns";
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle,
  DialogDescription,
  DialogFooter
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectGroup, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Printer } from "lucide-react";

// Interfaces defined locally for clarity
interface OrderItem {
  id: string;
  price: number;
  quantity: number;
  product_id: string;
  variant_id: string | null;
  product_name: string;
  variant_name: string | null;
}

interface Order {
  id: string;
  customer_name: string;
  customer_phone: string;
  customer_note: string | null;
  payment_method: string;
  payment_receipt: string | null;
  pix_key: string | null;
  status: string;
  total: number;
  created_at: string;
  items: OrderItem[]; // Expecting an array of OrderItem
}

interface OrderDetailsDialogProps {
  orderId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

// Order status map for badge colors
const statusColorMap: Record<string, string> = {
  pending: "bg-yellow-100 text-yellow-800",
  processing: "bg-blue-100 text-blue-800",
  shipped: "bg-purple-100 text-purple-800",
  delivered: "bg-green-100 text-green-800",
  cancelled: "bg-red-100 text-red-800",
};

// Order status labels in Portuguese
const statusLabels: Record<string, string> = {
  pending: "Pendente",
  processing: "Em Preparação",
  shipped: "Enviado",
  delivered: "Entregue",
  cancelled: "Cancelado",
};

export const OrderDetailsDialog: React.FC<OrderDetailsDialogProps> = ({
  orderId,
  open,
  onOpenChange,
}) => {
  const [printFormat, setPrintFormat] = React.useState<'a4' | '80mm' | '58mm'>('a4');
  const { data: order, isLoading, refetch } = useQuery<Order>({
    queryKey: ["order", orderId],
    queryFn: () => fetchOrderById(orderId),
    enabled: !!orderId && open, // Only fetch when dialog is open and orderId is available
  });

  // --- Add console log for debugging ---
  React.useEffect(() => {
    if (order) {
      console.log("Order data received in Dialog:", JSON.stringify(order, null, 2)); 
    }
    if (isLoading) {
       console.log("Order Dialog: Query is loading...");
    }
     if (!order && !isLoading) {
         console.log("Order Dialog: No order data found after loading.");
     }
  }, [order, isLoading]);
  // --- End console log ---

  const handleStatusChange = async (newStatus: string) => {
    try {
      await updateOrderStatus(orderId, newStatus);
      toast.success("Status do pedido atualizado com sucesso!");
      refetch(); // Refresh the order data
    } catch (error) {
      toast.error("Erro ao atualizar status do pedido");
      console.error("Error updating order status:", error);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  return (
    <>
      <style>{`
        @media print {
          @page {
            margin: ${printFormat === 'a4' ? '10mm' : '5mm'};
            size: ${printFormat === 'a4' ? 'A4' : printFormat === '80mm' ? '80mm auto' : '58mm auto'};
          }
          
          html, body {
            margin: 0 !important;
            padding: 0 !important;
            width: 100%;
            height: 100%;
          }
          
          body {
            display: flex !important;
            flex-direction: column !important;
            align-items: flex-start !important;
            justify-content: flex-start !important;
          }
          
          body > * {
            display: none !important;
          }
          
          #printable-order-details {
            display: block !important;
            position: static !important;
            width: 100% !important;
            margin: 0 !important;
            padding: 0 !important;
            background: white !important;
            visibility: visible !important;
          }
          
          #printable-order-details,
          #printable-order-details *,
          #printable-order-details *::before,
          #printable-order-details *::after {
            visibility: visible !important;
            display: revert !important;
          }
          
          .print-hidden {
            display: none !important;
          }
          
          .print-header {
            width: 100%;
            text-align: center;
            border-bottom: 2px solid #000;
            padding-bottom: ${printFormat === 'a4' ? '5mm' : '3mm'};
            margin-bottom: ${printFormat === 'a4' ? '5mm' : '3mm'};
            page-break-after: avoid;
          }
        }
      `}</style>
      
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-3xl print:max-w-full print:shadow-none print:border-none print:p-0">
          {/* Header: Hidden on print */}
          <DialogHeader className="print:hidden">
            <DialogTitle className="text-xl font-bold flex items-center justify-between">
              Detalhes do Pedido
              {order?.status && (
                <Badge variant="outline" className={statusColorMap[order.status]}>
                  {statusLabels[order.status] || order.status}
                </Badge>
              )}
            </DialogTitle>
          <DialogDescription>
            {!isLoading && order ? (
              <div className="text-sm text-gray-500">
                Pedido #{order.id.substring(0, 8)} •{" "}
                {format(new Date(order.created_at), "dd/MM/yyyy 'às' HH:mm")}
              </div>
            ) : (
              <Skeleton className="h-4 w-40" />
            )}
          </DialogDescription>
        </DialogHeader>

        {/* Printable Area */}
        <div id="printable-order-details" className="print:text-black print:bg-white">
          
          {/* Print Header - Aparece no topo da página */}
          <div className="print-header hidden print:block">
            <h1 className={`font-bold ${printFormat === 'a4' ? 'text-2xl' : 'text-lg'}`}>ALFA PEIXES</h1>
            <p className={`${printFormat === 'a4' ? 'text-base' : 'text-sm'} mt-1`}>Sistema de Vendas</p>
            {order && (
              <>
                <h2 className={`font-bold mt-2 ${printFormat === 'a4' ? 'text-xl' : 'text-base'}`}>
                  PEDIDO #{order.id.substring(0, 8).toUpperCase()}
                </h2>
                <p className={`${printFormat === 'a4' ? 'text-sm' : 'text-xs'} mt-1`}>
                  {format(new Date(order.created_at), "dd/MM/yyyy 'às' HH:mm")}
                </p>
              </>
            )}
          </div>

          {isLoading || !order ? (
            // Loading Skeleton
            <div className="space-y-4 p-4">
              <Skeleton className="h-16 w-full" />
              <Skeleton className="h-60 w-full" />
            </div>
          ) : (
            // Order Details Content
            <div className="p-4 space-y-4 print:p-0 print:space-y-3">
              {/* Customer Information */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4 print:grid-cols-2 print:mb-6">
                <div>
                  <h3 className="font-semibold text-gray-900 mb-1 print:text-base">Informações do Cliente</h3>
                  <p className="text-sm text-gray-700 print:text-sm"><span className="font-medium">Nome:</span> {order.customer_name}</p>
                  <p className="text-sm text-gray-700 print:text-sm"><span className="font-medium">Telefone:</span> {order.customer_phone}</p>
                  {order.customer_note && (
                    <div className="mt-2">
                      <h4 className="font-medium text-sm text-gray-800 print:text-sm">Observações:</h4>
                      <p className="text-sm text-gray-700 whitespace-pre-line print:text-sm">{order.customer_note}</p>
                    </div>
                  )}
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-1 print:text-base">Informações de Pagamento</h3>
                  {/* Display payment method text correctly */} 
                  <p className="text-sm text-gray-700 print:text-sm"><span className="font-medium">Método:</span> {statusLabels[order.payment_method] || order.payment_method}</p>
                  {order.payment_method === 'pix' && order.pix_key && (
                     <p className="text-sm text-gray-700 print:text-sm"><span className="font-medium">Chave PIX Cliente:</span> {order.pix_key}</p>
                  )}
                  <p className="text-sm text-gray-700 font-semibold print:text-sm"><span className="font-medium">Total:</span> {order.total.toLocaleString("pt-BR", { style: "currency", currency: "BRL" })}</p>
                  {order.payment_receipt && (
                    <div className="mt-2 print:hidden"> {/* Hide receipt link on print */}
                      <h4 className="font-medium text-sm text-gray-800">Comprovante:</h4>
                      <a href={order.payment_receipt} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline text-sm">Ver Comprovante</a>
                    </div>
                  )}
                </div>
              </div>

              <Separator className="my-4 print:hidden" />

              {/* Order Items */}
              <div>
                <h3 className="font-semibold text-gray-900 mb-2 print:text-base print:mb-3">Itens do Pedido</h3>
                {(!order.items || order.items.length === 0) ? (
                   <p className="text-sm text-gray-500 italic print:text-black">Nenhum item encontrado para este pedido.</p>
                 ) : (
                  <Table className="print:text-xs">
                    <TableHeader className="print:bg-gray-100">
                      <TableRow>
                        <TableHead className="print:p-1">Produto</TableHead>
                        <TableHead className="print:p-1">Variante</TableHead>
                        <TableHead className="text-center print:p-1">Qtd</TableHead>
                        <TableHead className="text-right print:p-1">Preço Un.</TableHead>
                        <TableHead className="text-right print:p-1">Subtotal</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {order.items.map((item) => (
                        <TableRow key={item.id} className="print:border-b">
                          <TableCell className="print:p-1">{item.product_name}</TableCell>
                          <TableCell className="print:p-1">{item.variant_name || "-"}</TableCell>
                          <TableCell className="text-center print:p-1">{item.quantity}</TableCell>
                          <TableCell className="text-right print:p-1">{Number(item.price).toLocaleString("pt-BR", { style: "currency", currency: "BRL" })}</TableCell>
                          <TableCell className="text-right font-medium print:p-1">{(Number(item.price) * item.quantity).toLocaleString("pt-BR", { style: "currency", currency: "BRL" })}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                 )}
                <div className="flex justify-end mt-4 print:mt-6">
                  <div className="text-right">
                    <div className="text-gray-600 print:text-sm">Total</div>
                    <div className="text-xl font-bold print:text-lg">
                      {order.total.toLocaleString("pt-BR", { style: "currency", currency: "BRL" })}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div> {/* End of printable area */} 

        {/* Footer: Hidden on print */}
        <DialogFooter className="mt-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 print:hidden p-4 border-t">
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 w-full sm:w-auto">
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium">Status:</span>
              <Select value={order?.status || ''} onValueChange={handleStatusChange} disabled={isLoading || !order}>
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="Selecione o status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectGroup>
                    {Object.entries(statusLabels).map(([value, label]) => (
                      <SelectItem key={value} value={value}>{label}</SelectItem>
                    ))}
                  </SelectGroup>
                </SelectContent>
              </Select>
            </div>
            
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium">Formato:</span>
              <Select value={printFormat} onValueChange={(value: 'a4' | '80mm' | '58mm') => setPrintFormat(value)}>
                <SelectTrigger className="w-[140px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="a4">A4 (210mm)</SelectItem>
                  <SelectItem value="80mm">Bobina 80mm</SelectItem>
                  <SelectItem value="58mm">Bobina 58mm</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          
          <div className="flex gap-2">
             {/* --- Print Button --- */}
             <Button variant="outline" onClick={handlePrint} disabled={isLoading || !order}>
               <Printer className="mr-2 h-4 w-4" />
               Imprimir
             </Button>
             <Button variant="outline" onClick={() => onOpenChange(false)}>
               Fechar
             </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
    </>
  );
};
